import { directorToken } from '@/utils/environment'
import { transformOutgoingStep } from '@/utils/dataTransformers'
import * as Sentry from '@/core/sentry'
import { http } from '@/utils/api'
import axios from 'axios'
import { useAuthStore } from '@/stores/auth/store'
import type { Extension } from '@/stores/extension/api'
import type { Tutorial } from '@/stores/tutorials/api'
import { isWhitelistError } from '@/utils/api/whitelist'
import { loadJob, type JobResponse } from '../job/api'

export type RecordPromptResult = {
  status: 'ok' | 'cancelled'
  type: string
  promptId: number
  audioFileId?: number
  extensionId?: number
  id?: number
  name?: string
}

export function withPbx(path: string) {
  const ctaId = useAuthStore().ctaId

  return `pbxes/${ctaId}/${path}`
}

// #### CTA REQUESTS

export type TapiResponse = {
  id: number
  ips: string[]
  password: string
  createdAt: string
  updatedAt: string
  pbxId: number
  username: string
}

export async function updateTapiSettings(tapiSettings: any) {
  try {
    const url = withPbx('tapi-connection')

    const response = await http.post<TapiResponse>(url, tapiSettings)

    return {
      status: 'success' as const,
      payload: response,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function deleteTapiSettings(tapiId: string) {
  try {
    const url = withPbx(`tapi-connection/${tapiId}`)

    const response = await http.delete(url)

    return {
      status: 'success' as const,
      payload: response,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export type Tariff = {
  type: string
  name: string
  id: number
  status: string
  extensionId: number
  promptId: number
  audioFileId: number
}

export async function updatePlan(planShortcode: any) {
  try {
    const url = withPbx('tariff')

    const { data } = await http.post<Tariff>(url, planShortcode)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

// #### ANALYTICS REQUESTS
export async function loadAnalyticsLink() {
  try {
    const url = withPbx('analytics/login')

    const { data } = await http.post(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function activateAnalytics() {
  try {
    const url = withPbx('analytics/activate')

    const { data } = await http.put(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function deactivateAnalytics() {
  try {
    const url = withPbx('analytics/deactivate')

    const { data } = await http.put(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function loadAnalyticsDPA() {
  try {
    const url = withPbx('analytics/dpa')

    const { data } = await http.get(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

// #### EXTENSION REQUESTS
export async function loadExtensions() {
  try {
    const url = withPbx('extensions')

    const { data } = await http.get<Extension[]>(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

// #### SIMPLE FORWARDING REQUESTS

export type Profile = {
  name: string
  color: string
  extensionId: number
  updatedAt: string
  createdAt: string
  id: number
  extension: Extension
}

// #### EXTENDED FORWARDING REQUESTS
export async function createProfile(extensionId: string, profileData: any) {
  try {
    const url = withPbx(`extensions/${extensionId}/profiles`)

    const { data } = await http.post<Profile>(url, profileData)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function updateProfile(
  extensionId: string,
  profileId: string,
  profileData: any
) {
  try {
    const url = withPbx(`extensions/${extensionId}/profiles/${profileId}`)

    const { data } = await http.put<Profile>(url, profileData)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function deleteProfile(extensionId: string, profileId: string) {
  try {
    const url = withPbx(`extensions/${extensionId}/profiles/${profileId}`)

    const { data } = await http.delete(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function createStep(
  extensionId: string,
  profileId: string,
  stepData: any
) {
  try {
    const url = withPbx(
      `extensions/${extensionId}/profiles/${profileId}/forwardings`
    )

    const { data } = await http.post(url, transformOutgoingStep(stepData))

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function updateStep(
  extensionId: string,
  profileId: string,
  stepId: string,
  stepData: any
) {
  try {
    const url = withPbx(
      `extensions/${extensionId}/profiles/${profileId}/forwardings/${stepId}`
    )

    const { data } = await http.put(url, transformOutgoingStep(stepData))

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function deleteStep(
  extensionId: string,
  profileId: string,
  stepId: string
) {
  try {
    const url = withPbx(
      `extensions/${extensionId}/profiles/${profileId}/forwardings/${stepId}`
    )

    const { data } = await http.delete(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function saveTimeslots(profileId: string, profileData: any) {
  try {
    const { name, color, extensionId, timeslots } = {
      ...profileData,
      timeslots: profileData.timeslots ?? [],
    } as any

    const url = withPbx(`extensions/${extensionId}/profiles/${profileId}`)

    const { data } = await http.patch(url, {
      name,
      color,
      timeslots,
    })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

// #### AUDIO-FILES REQUESTS

const CancelToken = axios.CancelToken
let source = CancelToken.source()

type RecordJobCreationResponse = {
  job_id: number
  status_url: string
}

function parseJobResult(result: string) {
  try {
    return JSON.parse(result)
  } catch {
    return result
  }
}

function createRecordCancelSource() {
  source = CancelToken.source()

  return source
}

async function startRecordJob<T>(
  url: string,
  prompt: any,
  onStatusChange?: (status: JobResponse['status']) => void
) {
  const recordCancelSource = createRecordCancelSource()

  const { data } = await http.post<RecordJobCreationResponse>(url, prompt, {
    cancelToken: recordCancelSource.token,
  })

  const result = await loadJob<T>(
    data.status_url,
    onStatusChange,
    recordCancelSource.token
  )

  if (result.status === 'success') {
    return result
  }

  if (result.status === 'job-error') {
    const error = new Error('Record prompt job failed') as Error & {
      jobResult?: typeof result
      response?: { status: number; data: unknown }
    }

    error.jobResult = result
    error.response = {
      status: result.payload.httpStatus ?? 500,
      data: parseJobResult(result.payload.result),
    }

    throw error
  }

  if (result.status === 'timeout-error') {
    const error = new Error('Record prompt job timed out') as Error & {
      response?: { status: number; data: unknown }
    }

    error.response = {
      status: 504,
      data: { message: 'Record prompt job timed out' },
    }

    throw error
  }

  throw result.payload
}

export async function promptExist(
  promptId: number | string,
  prompt: any,
  onStatusChange?: (status: JobResponse['status']) => void
) {
  try {
    const url = withPbx(`audio-files/record-job/${promptId}`)

    const result = await startRecordJob<RecordPromptResult>(
      url,
      prompt,
      onStatusChange
    )

    return {
      status: 'success' as const,
      payload: result.payload,
    }
  } catch (error) {
    if (!isWhitelistError(error) && !axios.isCancel(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function promptDoesntExist(
  prompt: any,
  onStatusChange?: (status: JobResponse['status']) => void
) {
  try {
    const url = withPbx('audio-files/record-job')

    const result = await startRecordJob<RecordPromptResult>(
      url,
      prompt,
      onStatusChange
    )

    return {
      status: 'success' as const,
      payload: result.payload,
    }
  } catch (error: any) {
    if (!axios.isCancel(error)) {
      Sentry.captureException(error)

      return {
        status: 'error' as const,
        payload: error,
      }
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function cancelRecordPrompt() {
  try {
    source.cancel('Record canceled by the user.')

    return {
      status: 'canceled' as const,
      payload: {
        fn: cancelRecordPrompt.name,
        message: 'Record canceled by the user.',
      },
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

// #### RESET PASSWORD
export async function saveResetPassword(inputData: any) {
  try {
    const { data } = await http.post('/activate', inputData, {})

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function resendOfficeDeskEmail(inputData: any) {
  try {
    const url = withPbx(`users/${inputData.id}`)

    const { data } = await http.put(url, inputData, {
      headers: {
        'auth-token': directorToken() ?? '',
      },
    })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

// #### BACKUP REQUESTS

// #### CFC REQUESTS

export async function loadTutorial(tutorialId: string) {
  try {
    const url = withPbx(`tutorial-progress/${tutorialId}`)

    const { data } = await http.get<Tutorial>(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}
